import { authorizedFetch } from './utils';
import appConfig from '../config/appConfig';

const baseApi = appConfig.api.baseUrl;

export async function postQuery({ searchTerm, location, searchType, page = 1, queryId = null, showLoader = true }) {
  const requestBody = { searchTerm, location, searchType, page };
  
  // Add queryId to request body if provided (for pagination)
  if (queryId) {
    requestBody.queryId = queryId;
  }
  
  const response = await authorizedFetch(`${baseApi}/api/Queries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
    showLoader,
  });
  if (!response.ok) {
    // Try to get the server error message
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.text();
      if (errorData) {
        // Try to parse as JSON first
        try {
          const jsonError = JSON.parse(errorData);
          errorMessage = jsonError.message || jsonError.error || jsonError.title || jsonError;
        } catch {
          // If not JSON, use the text as is
          errorMessage = errorData;
        }
      }
    } catch {
      // Fallback to generic message
    }
    
    // Create error object with status information
    const error = new Error(errorMessage);
    error.status = response.status;
    error.response = { status: response.status };
    throw error;
  }
  return response.json();
}

export async function getQueries(status = 1, queryStatus, showLoader = true) {
  let url = `${baseApi}/api/Queries?status=${status}`;
  if (queryStatus !== undefined) {
    url += `&queryStatus=${queryStatus}`;
  }
  const response = await authorizedFetch(url, { showLoader });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export async function getQueryResults(queryId, status = null, showLoader = true) {
  let url = `${baseApi}/api/Queries/${queryId}/results`;
  if (status !== null) {
    url += `?status=${status}`;
  }
  const response = await authorizedFetch(url, { showLoader });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export async function updateQuery(queryId, status) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/${queryId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export async function updateResultStatus(resultId, status, error = null) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/results/${resultId}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status, error }),
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export async function crawlQueryResult(queryResultId, showLoader = false) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/results/${queryResultId}/crawl`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    showLoader,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  
  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    // Return empty object for successful empty responses
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON response: ${error.message}`);
  }
}

export async function saveQuerySetup({ queryId, aiNotes, queryResultId, generatedText }) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/setup`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      queryId, 
      aiNotes, 
      queryResultId, 
      generatedText 
    }),
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  
  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    // Return empty object for successful empty responses
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON response: ${error.message}`);
  }
}

export async function getQueryResultSetup(queryResultId) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/results/${queryResultId}/setup`);
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  
  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    // Return empty object for successful empty responses
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON response: ${error.message}`);
  }
}

export async function generateText({ notes, queryResultId, showLoader = true }) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/generate-text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      notes, 
      queryResultId 
    }),
    showLoader,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  
  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    // Return empty object for successful empty responses
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON response: ${error.message}`);
  }
}

export async function importUrls({ searchTerm, urls, showLoader = true }) {
  const response = await authorizedFetch(`${baseApi}/api/Queries/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      searchTerm, 
      urls 
    }),
    showLoader,
  });
  if (!response.ok) {
    // Try to get the server error message
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.text();
      if (errorData) {
        try {
          const jsonError = JSON.parse(errorData);
          errorMessage = jsonError.message || jsonError.error || jsonError.title || jsonError;
        } catch {
          errorMessage = errorData;
        }
      }
    } catch {
      // Fallback to generic message
    }
    throw new Error(errorMessage);
  }
  
  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON response: ${error.message}`);
  }
}

export { baseApi }; 